<script setup>
/**
 * 首屏加载页 — Icon Cloud 绕 Logo 旋转，收拢后飞入顶栏
 * 至少转满一圈再收拢；加载慢放慢旋转，加载快加速收拢/飞行
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { GatheringIconCloud } from '@/components/inspira/icon-cloud';
import { SUPPORTED_CHANNELS } from '@/config/channels.js';
import { BRAND_LOGO_SRC } from '@/config/brand.js';

const props = defineProps({
  loadProgress: { type: Number, default: 0 },
  pageLoaded: { type: Boolean, default: false },
});

const emit = defineEmits(['revealing', 'finished']);

const channels = SUPPORTED_CHANNELS;
const channelImages = computed(() => channels.map((c) => c.icon).filter(Boolean));

const logoAnchorRef = useTemplateRef('logoAnchor');
const flyLogoRef = useTemplateRef('flyLogo');

const gathering = ref(false);
const gathered = ref(false);
const flying = ref(false);
const flyActive = ref(false);
const flyStyle = ref({});
const animationSpeed = ref(1);
const rotationLapDone = ref(false);

const GATHER_ROUTES = [
  { midR: 0.3, duration: 820, delay: 0 },
  { midR: 0.22, duration: 1040, delay: 90 },
  { midR: 0.33, duration: 1120, delay: 25 },
  { midR: 0.17, duration: 780, delay: 145 },
  { midR: 0.25, duration: 980, delay: 55 },
  { midR: 0.31, duration: 1160, delay: 115 },
  { midR: 0.19, duration: 740, delay: 185 },
  { midR: 0.28, duration: 920, delay: 75 },
  { midR: 0.35, duration: 1200, delay: 15 },
];

const GATHER_PAUSE = 380;
const FLY_DURATION = 680;
const CLOUD_SIZE = 520;
const GATHER_MAX = Math.max(
  ...GATHER_ROUTES.map(({ duration, delay }) => duration + delay),
);

const timers = [];
let rafId = 0;
let lastFrame = 0;
let gatherAnimTime = 0;
let pauseAnimTime = 0;
let phaseSpeed = 1;

/** 加载慢时放慢（0.65~1），加载完成时加速（1.75~2.5） */
function resolveSpeedFactor(pageLoaded, loadProgress) {
  if (pageLoaded) return Math.min(2.5, 1.75 + loadProgress * 0.75);
  return 0.65 + loadProgress * 0.35;
}

/** 旋转阶段：资源就绪后加速转完剩余圈数 */
function rotationSpeedFactor() {
  return resolveSpeedFactor(props.pageLoaded, props.loadProgress);
}

function startGathering() {
  if (gathering.value) return;
  gathering.value = true;
  gatherAnimTime = 0;
  phaseSpeed = resolveSpeedFactor(props.pageLoaded, props.loadProgress);
  animationSpeed.value = phaseSpeed;
}

function startGathered() {
  if (gathered.value) return;
  gathered.value = true;
  pauseAnimTime = 0;
  phaseSpeed = resolveSpeedFactor(props.pageLoaded, props.loadProgress);
  animationSpeed.value = phaseSpeed;
}

async function flyToHeader(speed) {
  const flyMs = Math.max(280, Math.round(FLY_DURATION / speed));
  const fromEl = logoAnchorRef.value;
  const toEl = document.querySelector('[data-header-logo]');

  if (!fromEl || !toEl) {
    emit('finished');
    return;
  }

  const from = fromEl.getBoundingClientRect();
  const to = toEl.getBoundingClientRect();
  const fromX = from.left + from.width / 2;
  const fromY = from.top + from.height / 2;
  const toX = to.left + to.width / 2;
  const toY = to.top + to.height / 2;
  const scale = to.width / from.width;

  flying.value = true;
  emit('revealing');
  flyStyle.value = {
    width: `${from.width}px`,
    height: `${from.height}px`,
    '--fly-from': `translate3d(${fromX}px, ${fromY}px, 0) translate(-50%, -50%) scale(1)`,
    '--fly-to': `translate3d(${toX}px, ${toY}px, 0) translate(-50%, -50%) scale(${scale})`,
    '--fly-radius': `${12 / scale}px`,
    '--fly-duration': `${flyMs}ms`,
  };

  await nextTick();
  const el = flyLogoRef.value;
  if (!el) {
    emit('finished');
    return;
  }

  void el.offsetWidth;
  flyActive.value = true;
  timers.push(setTimeout(() => emit('finished'), flyMs));
}

function tick(now) {
  if (flying.value) return;

  if (!lastFrame) lastFrame = now;
  const dt = now - lastFrame;
  lastFrame = now;

  if (!gathering.value) {
    animationSpeed.value = rotationSpeedFactor();

    if (rotationLapDone.value) {
      startGathering();
    }
  } else if (!gathered.value) {
    gatherAnimTime += dt * phaseSpeed;
    if (gatherAnimTime >= GATHER_MAX) {
      startGathered();
    }
  } else {
    pauseAnimTime += dt * phaseSpeed;
    if (pauseAnimTime >= GATHER_PAUSE) {
      flyToHeader(phaseSpeed);
      return;
    }
  }

  rafId = requestAnimationFrame(tick);
}

onMounted(() => {
  phaseSpeed = resolveSpeedFactor(props.pageLoaded, props.loadProgress);
  animationSpeed.value = phaseSpeed;
  rafId = requestAnimationFrame(tick);
});

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId);
  timers.forEach(clearTimeout);
});
</script>

<template>
  <div
    class="fixed inset-0 z-[100] transition-colors duration-500"
    :class="flying ? 'bg-transparent' : 'bg-background'"
  >
    <div v-show="!flying" class="flex size-full items-center justify-center">
      <div class="relative size-[min(94vw,34rem)]">
        <GatheringIconCloud
          :images="channelImages"
          :gathering="gathering"
          :gathered="gathered"
          :gather-routes="GATHER_ROUTES"
          :speed-factor="animationSpeed"
          :size="CLOUD_SIZE"
          class="mx-auto block"
          @rotation-lap="rotationLapDone = true"
        />

        <!-- 隐形锚点：Logo 在 Canvas 内绘制，此处仅用于飞行动画定位 -->
        <span
          ref="logoAnchor"
          class="pointer-events-none absolute top-1/2 left-1/2 size-20 -translate-x-1/2 -translate-y-1/2 opacity-0"
          aria-hidden="true"
        />
      </div>
    </div>

    <div
      v-if="flying"
      ref="flyLogo"
      class="fly-logo pointer-events-none fixed top-0 left-0 z-[101] grid origin-center place-items-center overflow-hidden shadow-xl shadow-primary/30 will-change-transform"
      :class="{ 'is-flying': flyActive }"
      :style="flyStyle"
    >
      <img :src="BRAND_LOGO_SRC" alt="" class="size-full rounded-full object-cover" />
    </div>
  </div>
</template>

<style scoped>
.fly-logo {
  border-radius: 50%;
  transform: var(--fly-from);
  transition: none;
}

.fly-logo.is-flying {
  border-radius: var(--fly-radius);
  transform: var(--fly-to);
  transition:
    transform var(--fly-duration) cubic-bezier(0.4, 0, 0.2, 1),
    border-radius var(--fly-duration) cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
